/**
 * @file    FieldDetailsTab.tsx
 * @scope   VIEW
 * @summary The "Details" tab of a field card: field picker, display name, calculation type and the
 *          per-calc-type controls (precision, normalize, stat testing, L1/L2, advanced link).
 * @consumes FieldsManager models + FieldStateModel, percentage-settings validation
 * @produces `FieldDetailsTab` component
 * @sideEffects None — controlled inputs; changes bubble up via callbacks.
 * @notes Control visibility/disabled is driven entirely by the passed `fieldState` (computed by the
 *        field-state hooks), so this file never re-derives calc-type rules.
 */
import React from 'react';
import Select from 'react-select';
import Tippy from '@tippyjs/react';
import { SelectedFieldModel, FieldStateModel } from 'models/Enterprise/FieldsManagerModel';
import { decimalPlaces } from 'constants/chartOption';
import { isPercentageSettingsIncomplete } from '../../utils/validation/fieldValidation';
import OptionCheckbox from 'core-ui/optionCheckbox/optionCheckbox';

const NORMALIZE_TOOLTIP =
  "Excludes non-answers (Don't Know/Not Applicable/Refuse) from the selected calculation";

const customSelectStyles = {
  control: (styles: any) => ({
    ...styles,
    maxWidth: '100%',
  }),
};

interface FieldDetailsTabProps {
  field: SelectedFieldModel;
  index: number;
  fieldState: FieldStateModel;
  isTouched: boolean;
  availableFields: { value: string; label: string }[];
  calculationTypes: { value: string; label: string }[];
  onFieldSelect: (index: number, option: any) => void;
  onDisplayNameChange: (index: number, value: string) => void;
  onCalculationTypeChange: (index: number, option: any) => void;
  onPrecisionChange: (index: number, option: any) => void;
  onNormalizeChange: (index: number, checked: boolean) => void;
  onStatTestingChange: (index: number, checked: boolean) => void;
  onIsL1CategoryChange: (index: number, checked: boolean) => void;
  onAdvancedSettings?: (field: SelectedFieldModel, fieldIndex: number) => void;
}

/** Renders the field configuration controls (select, calculation type, display name, etc.). */
const FieldDetailsTab: React.FC<FieldDetailsTabProps> = ({
  field,
  index,
  fieldState,
  isTouched,
  availableFields,
  calculationTypes,
  onFieldSelect,
  onDisplayNameChange,
  onCalculationTypeChange,
  onPrecisionChange,
  onNormalizeChange,
  onStatTestingChange,
  onIsL1CategoryChange,
  onAdvancedSettings,
}) => {
  return (
    <div>
      {/* Field Select */}
      {fieldState.SelectField.visible && (
        <Tippy
          content={
            <div>
              <div className="tooltip-title">{field.field?.displayName?.trim() ?? ''}</div>
              <div className="tooltip-text">{field.field.longText?.trim() ?? ''}</div>
            </div>
          }
          disabled={!field.field?.displayName?.trim() || !field.field.longText?.trim()}
          placement="left"
          className="tooltip-container"
        >
          <div
            className={`diy-input-container ${
              fieldState.SelectField.disabled ? 'field-disabled' : ''
            }${isTouched && !field.field?.fieldGuid ? ' field-input--invalid' : ''}`}
          >
            <div className="diy-material-textfield">
              <Select
                className={`diy-select${
                  isTouched && !field.field?.fieldGuid ? ' diy-select--invalid' : ''
                }`}
                options={availableFields}
                value={
                  field.field.fieldGuid
                    ? availableFields.find((f) => f.value === field.field.fieldGuid)
                    : null
                }
                onChange={(option) => onFieldSelect(index, option)}
                styles={customSelectStyles}
                placeholder={
                  isTouched && !field.field?.fieldGuid ? 'Field is required.' : 'Select a field...'
                }
                isDisabled={fieldState.SelectField.disabled}
              />
              <label
                className={`diy-label ${
                  fieldState.SelectField.disabled ? 'field-label-disabled' : ''
                }`}
              >
                Field
              </label>
            </div>
          </div>
        </Tippy>
      )}

      {/* Calculation Type */}
      {fieldState.CalculationType.visible && (
        <div
          className={`diy-input-container ${
            fieldState.CalculationType.disabled ? 'field-disabled' : ''
          }${isTouched && !field.calculationType ? ' field-input--invalid' : ''}`}
        >
          <div className="diy-material-textfield">
            <Select
              className={`diy-select${
                isTouched && !field.calculationType ? ' diy-select--invalid' : ''
              }`}
              options={calculationTypes}
              value={
                field.calculationType
                  ? calculationTypes.find(
                      (ct) => String(ct.value) === String(field.calculationType?.type)
                    )
                  : null
              }
              isDisabled={fieldState.CalculationType.disabled}
              onChange={(option) => onCalculationTypeChange(index, option)}
              styles={customSelectStyles}
              placeholder={
                isTouched && !field.calculationType
                  ? 'Calculation Type is required.'
                  : 'Select calculation type...'
              }
            />
            <label
              className={`diy-label ${
                fieldState.CalculationType.disabled ? 'field-label-disabled' : ''
              }`}
            >
              Calculation Type
            </label>
          </div>
        </div>
      )}

      {/* Advanced Settings Link */}
      {fieldState.AdvancedSettings.visible && onAdvancedSettings && (
        <div
          className={`manage-fields-container${
            isTouched && isPercentageSettingsIncomplete(field) ? ' field-input--invalid' : ''
          }`}
        >
          <Tippy
            content="You must select at least one advanced settings value for this configuration"
            placement="left"
            className="tooltip-container"
            disabled={!isTouched || !isPercentageSettingsIncomplete(field)}
          >
            <a
              href="#"
              className={`manage-fields-link advanced-settings-link${
                isTouched && isPercentageSettingsIncomplete(field) ? ' diy-required-field' : ''
              }`}
              onClick={(e) => {
                e.preventDefault();
                onAdvancedSettings(field, index);
              }}
            >
              {isPercentageSettingsIncomplete(field)
                ? 'Advanced settings (required)'
                : 'Advanced settings'}
            </a>
          </Tippy>
        </div>
      )}

      {/* Display Name */}
      {fieldState.DisplayName.visible && (
        <div
          className={`diy-input-container ${
            fieldState.DisplayName.disabled ? 'field-disabled' : ''
          }${isTouched && !field.displayName?.trim() ? ' field-input--invalid' : ''}`}
        >
          <Tippy
            content="This text will be visible on the table"
            placement="left"
            className="tooltip-container"
          >
            <div className="diy-material-textfield">
              <input
                className="diy-input"
                id={`display-name-${index}`}
                type="text"
                placeholder={
                  isTouched && !field.displayName?.trim() ? 'Display Name is required.' : ''
                }
                value={field.displayName}
                onFocus={() => {
                  if (field.displayName === 'New Field') {
                    onDisplayNameChange(index, '');
                  }
                }}
                onChange={(e) => onDisplayNameChange(index, e.target.value)}
                maxLength={50}
                disabled={fieldState.DisplayName.disabled}
              />
              <label
                className={`diy-label ${
                  fieldState.DisplayName.disabled ? 'field-label-disabled' : ''
                }`}
              >
                Display Name
              </label>
            </div>
          </Tippy>
        </div>
      )}

      {/* Precision */}
      {fieldState.Precision.visible && (
        <div className={`option-item ${fieldState.Precision.disabled ? 'field-disabled' : ''}`}>
          <span className="toggler-label">Decimal Places</span>
          <Select
            className="diy-select"
            options={decimalPlaces}
            name="precision"
            value={decimalPlaces.find((dp) => dp.value === parseInt(field.precision) || 0)}
            onChange={(option) => onPrecisionChange(index, option)}
            styles={customSelectStyles}
            isDisabled={fieldState.Precision.disabled}
          />
        </div>
      )}

      {/* Checkboxes */}
      <div className="form-checkboxes">
        {fieldState.isL1Category.visible && (
          <OptionCheckbox
            option={{
              displayName: 'Left and Bold Text',
              actionName: `highlight-${index}`,
              isChecked: field.isL1Category === true,
            }}
            onChangeHandler={(e: React.ChangeEvent<HTMLInputElement>) =>
              onIsL1CategoryChange(index, e.target.checked)
            }
            size="small"
          />
        )}
        {fieldState.Normalize.visible && (
          <Tippy content={NORMALIZE_TOOLTIP} placement="left" className="tooltip-container">
            <div>
              <OptionCheckbox
                option={{
                  displayName: 'Exclude Non-Answer Responses',
                  actionName: `normalize-${index}`,
                  isChecked: field.normalize,
                }}
                onChangeHandler={(e: React.ChangeEvent<HTMLInputElement>) =>
                  onNormalizeChange(index, e.target.checked)
                }
                size="small"
              />
            </div>
          </Tippy>
        )}
        {fieldState.StatTesting.visible && (
          <OptionCheckbox
            option={{
              displayName: 'Stat Testing',
              actionName: `stat-testing-${index}`,
              isChecked: field.statTesting,
            }}
            onChangeHandler={(e: React.ChangeEvent<HTMLInputElement>) =>
              onStatTestingChange(index, e.target.checked)
            }
            size="small"
          />
        )}
      </div>
    </div>
  );
};

export default FieldDetailsTab;
